import { Popover } from '@material-ui/core';
import { cloneDeep } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { FixedSizeList } from 'react-window';

import { ISelectOption, ISelectPreset } from '~/src/dash/components/dropdowns/common';
import { ActiveQuickFilter } from '~/src/dash/components/dropdowns/subComponents/ActiveQuickFilter';
import { EActiveFilter, statuses } from '~/src/dash/components/dropdowns/subComponents/common';
import { VirtualSelectRow } from '~/src/dash/components/dropdowns/VirtualSelectRow';
import { CaretMiniDownIcon } from '~/src/dash/components/icons/misc-icons';
import {
  borderRadius,
  Col,
  Cols,
  Row,
  Rows,
  spacing,
  Text,
  TextButton,
  TextInput,
  TKey,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { isLanguageOversized } from '~/src/locales/common';

const getHeight = (numItems: number): number => {
  if (numItems >= 12) return 360;
  return numItems * 30 + 8;
};

const determineTKey = (
  labelTKey: TKey,
  selectedIDs: Set<string | number>,
  totalCount: number,
  isPlaceholderAllSelected: boolean = false,
): TKey => {
  if (!labelTKey) {
    return '';
  }
  if (totalCount === 0) {
    return 'NoItemFound';
  }
  if (selectedIDs.size === 1) {
    return 'FormatSelection';
  }
  if ((isPlaceholderAllSelected && selectedIDs.size === 0) || selectedIDs.size === totalCount) {
    return 'AllItemsSelected';
  }
  if (selectedIDs.size === 0) {
    return 'SelectItem';
  }
  return 'FormatLabelWithCount';
};

const sortOptions = (
  options: ISelectOption[],
  selectedIDs: Set<number | string>,
  isSortDisabled: boolean = false,
  extractStringToSort?: (option: ISelectOption) => string | number,
): ISelectOption[] => {
  if (isSortDisabled) {
    return options;
  }

  const selectedOptions: ISelectOption[] = [];
  const unselectedOptions: ISelectOption[] = [];

  // Separate selected and unselected options
  options.forEach((option) => {
    if (selectedIDs.has(option.id)) {
      selectedOptions.push(option);
    } else {
      unselectedOptions.push(option);
    }
  });

  // Sort function
  const sortFn = (a: ISelectOption, b: ISelectOption): number => {
    const aValue = extractStringToSort ? extractStringToSort(a) : a.id;
    const bValue = extractStringToSort ? extractStringToSort(b) : b.id;
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return aValue - bValue;
    }
    return String(aValue).localeCompare(String(bValue));
  };

  // Sort both arrays
  selectedOptions.sort(sortFn);
  unselectedOptions.sort(sortFn);

  // Return selected options first, then unselected
  return [...selectedOptions, ...unselectedOptions];
};

const queryOptions = (query: string, options: ISelectOption[]): ISelectOption[] => {
  let queriedOptions = [...options];
  if (query) {
    const casedQuery = query.toLowerCase();
    queriedOptions = options.filter(
      (item) => String(item.id).includes(query) || item.label.toLowerCase().includes(casedQuery),
    );
  }
  return queriedOptions;
};

const extractActiveFilteredList = (
  options: ISelectOption[],
  activeStatus: EActiveFilter,
): ISelectOption[] => {
  switch (activeStatus) {
    case EActiveFilter.ACTIVE:
      return options.filter((item) => item.isActive);
    case EActiveFilter.INACTIVE:
      return options.filter((item) => !item.isActive);
    case EActiveFilter.SHOW_ALL:
    default:
      return options;
  }
};

interface IProps {
  disableSearch?: boolean;
  disableSort?: boolean;
  displayID?: boolean;
  enableActiveFilter?: boolean;
  enableLabelTooltip?: boolean;
  extractStringToSort?: (option: ISelectOption) => string | number;
  handleApplySelect: (value: Set<number | string>, key?: string | number) => void;
  isCheckboxHidden?: boolean;
  isDisabled?: boolean;
  isPlaceholderAllSelected?: boolean;
  isSelectionRequired?: boolean;
  key: string | number; // used for react purposes
  labelTKey?: TKey; // eventually will be required
  maxWidth?: number;
  onLoadDisplayActiveOnly?: boolean;
  options: ISelectOption[];
  popoverWidth?: number;
  selectKey: string | number; // options list key
  selectedLimit?: number;
  selectedOptionIDs: Set<number | string>;
  height?: number;
  minWidth?: number;
  textWidth?: number;
  presets?: ISelectPreset[];
  syncWithParent?: boolean;
}

export const IndependentSelectV2 = ({
  disableSearch = false,
  disableSort = false,
  displayID = false,
  enableActiveFilter = false,
  enableLabelTooltip = false,
  extractStringToSort,
  handleApplySelect,
  isCheckboxHidden = false,
  isDisabled = false,
  isPlaceholderAllSelected = false,
  isSelectionRequired = false,
  labelTKey,
  maxWidth = 240,
  onLoadDisplayActiveOnly = false,
  options,
  popoverWidth = 480,
  selectKey,
  selectedLimit = 1,
  selectedOptionIDs = new Set(),
  height = 32,
  minWidth = 240,
  presets,
  textWidth,
  syncWithParent = false,
}: IProps): React.ReactNode => {
  const { t, i18n } = usePublicaTranslation();

  const [anchorEl, setAnchorEl] = useState<(EventTarget & Element) | null>(null);
  const [sortedOptions, setSortedOptions] = useState<ISelectOption[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<ISelectOption[]>([]);
  const [search, setSearch] = useState('');
  const [selectedActiveStatus, setSelectedActiveStatus] = useState(
    onLoadDisplayActiveOnly ? EActiveFilter.ACTIVE : EActiveFilter.SHOW_ALL,
  );
  const [selectedIDs, setSelectedIDs] = useState<Set<number | string>>(selectedOptionIDs);
  const [errorMessageTKey, setErrorMessageTKey] = useState<TKey>('');
  const isMounted = useRef(false);
  const hasChangeOccurred = useRef(false);

  const isSingleSelect = selectedLimit === 1;
  const totalCount = options?.length || 0;
  const isSingleSelectionRequired = selectedLimit === 1 && isSelectionRequired;
  const isOversizedLanguage = isLanguageOversized(i18n.language);

  const handleApply = (selectKey?: string | number): void => {
    handleApplySelect(selectedIDs, selectKey);
  };

  useEffect(() => {
    const sorted = sortOptions(options || [], selectedIDs, disableSort, extractStringToSort);
    unstable_batchedUpdates(() => {
      setSortedOptions(sorted);
      setSearch('');
    });
  }, [options]);

  // filter options upon search
  useEffect(() => {
    if (isMounted.current) {
      const queriedOptions = queryOptions(search, sortedOptions);
      const activeFilteredList = extractActiveFilteredList(queriedOptions, selectedActiveStatus);
      setFilteredOptions(activeFilteredList);
    }
  }, [search, sortedOptions, selectedActiveStatus]);

  // if the dropdown closes immediately after a selection, handleApplySelect here instead
  useEffect(() => {
    if (isMounted.current && isSingleSelectionRequired) {
      handleApply(selectKey);
    }
    isMounted.current = true;
  }, [selectedIDs]);

  // Sync with parent state changes (controlled mode)
  useEffect(() => {
    if (syncWithParent) {
      // Deep equality check: only update if content actually changed
      const hasChanged =
        selectedOptionIDs.size !== selectedIDs.size ||
        ![...selectedOptionIDs].every((id) => selectedIDs.has(id));

      if (hasChanged) {
        const sorted = sortOptions(options, selectedOptionIDs, disableSort, extractStringToSort);
        unstable_batchedUpdates(() => {
          setSelectedIDs(selectedOptionIDs);
          setSortedOptions(sorted);
        });
      }
    }
  }, [selectedOptionIDs, options, disableSort, extractStringToSort, syncWithParent]);

  const handleOnOpen = (e: React.MouseEvent): void => {
    if (!isDisabled && totalCount) setAnchorEl(e.currentTarget);
  };

  const handleOnClose = (forceClose?: boolean): void => {
    if (
      (!forceClose && selectedIDs.size === 0) ||
      (isSingleSelectionRequired && selectedIDs.size === 0)
    ) {
      setErrorMessageTKey('SelectErrorMustSelectOption');
      return;
    }
    unstable_batchedUpdates(() => {
      if (errorMessageTKey) setErrorMessageTKey('');
      setSearch('');
      setAnchorEl(null);
    });
    if (!isSingleSelectionRequired) handleApply(selectKey);
  };

  const handleOnChange = (option: ISelectOption): void => {
    const { id } = option;
    const clonedSelectedIDs = new Set([...selectedIDs]);
    const selectedCount = clonedSelectedIDs.size;

    if (isSingleSelect) {
      if (clonedSelectedIDs.has(id)) {
        if (isSelectionRequired && selectedCount === 1) return;
        clonedSelectedIDs.delete(id);
      } else if (selectedCount === 1) {
        clonedSelectedIDs.clear();
        clonedSelectedIDs.add(id);
      } else {
        clonedSelectedIDs.add(id);
      }
      const sorted = sortOptions(options, clonedSelectedIDs, disableSort, extractStringToSort);
      hasChangeOccurred.current = true;
      unstable_batchedUpdates(() => {
        setSortedOptions(sorted);
        setSelectedIDs(clonedSelectedIDs);
      });
      if (isSelectionRequired) {
        if (clonedSelectedIDs.size === 0) {
          setErrorMessageTKey('SelectErrorMustSelectOption');
          return;
        } else {
          handleOnClose(true);
        }
      }
    } else if (
      !selectedLimit ||
      clonedSelectedIDs.has(id) ||
      (!clonedSelectedIDs.has(id) && selectedLimit && selectedCount < selectedLimit)
    ) {
      if (clonedSelectedIDs.has(id)) {
        clonedSelectedIDs.delete(id);
      } else {
        clonedSelectedIDs.add(id);
      }
      const sorted = sortOptions(options, clonedSelectedIDs, disableSort, extractStringToSort);
      hasChangeOccurred.current = true;

      unstable_batchedUpdates(() => {
        setSortedOptions(sorted);
        setSelectedIDs(clonedSelectedIDs);
      });
    }
  };

  const handleSelectAll = (): void => {
    const clonedSelectedIDs = cloneDeep(selectedIDs);
    filteredOptions.forEach(({ id }) => {
      clonedSelectedIDs.add(id);
    });
    const sorted = sortOptions(options, clonedSelectedIDs, disableSort, extractStringToSort);
    hasChangeOccurred.current = true;

    unstable_batchedUpdates(() => {
      setSortedOptions(sorted);
      setSelectedIDs(clonedSelectedIDs);
    });
  };

  const handleClear = (): void => {
    const clearedSelectedIDs = new Set([]);
    const sorted = sortOptions(options, clearedSelectedIDs, disableSort, extractStringToSort);
    hasChangeOccurred.current = true;
    unstable_batchedUpdates(() => {
      setSortedOptions(sorted);
      setSelectedIDs(clearedSelectedIDs);
    });
  };

  const handleSelectPreset = (presetIDs: Set<string | number>): void => {
    const sorted = sortOptions(options, presetIDs, disableSort, extractStringToSort);
    hasChangeOccurred.current = true;
    unstable_batchedUpdates(() => {
      setSortedOptions(sorted);
      setSelectedIDs(presetIDs);
    });
  };

  const handleChangeActiveFilter = (status: EActiveFilter): void => {
    setSelectedActiveStatus(status);
  };

  const getSelectionValue = (): string => {
    const selected = options.find((option) => selectedIDs.has(option.id));
    if (!selected) {
      return '';
    }
    if (selected.tKey) {
      return t(selected.tKey, selected.tProps || {});
    }
    return selected.label;
  };

  const listCount = options?.length || 0;
  const filteredlistCount = filteredOptions.length;
  const selectedCount = selectedIDs.size;
  const selectedMax = selectedLimit && selectedLimit < listCount ? selectedLimit : listCount;

  return (
    <>
      <Cols
        extraSmallVerticalPadding
        horizontalPadding
        onClick={handleOnOpen}
        gap={spacing.small}
        style={{
          alignItems: 'center',
          maxWidth: `${maxWidth}px`,
          minWidth: `${minWidth}px`,
          cursor: isDisabled || !totalCount ? 'not-allowed' : 'pointer',
          justifyContent: 'space-between',
          borderRadius: borderRadius.base,
          height: `${height}px`,
          background: publicaColors.white,
          border: `1px solid ${publicaColors.lightGray}`,
          transition: 'all 0.5s ease-in-out',
        }}
      >
        <Col style={{ flex: 1, minWidth: 0 }}>
          <Text
            small
            color={isDisabled || !totalCount ? publicaColors.lightGray : 'inherit'}
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              paddingRight: spacing.small,
            }}
            tKey={
              labelTKey
                ? determineTKey(labelTKey, selectedIDs, totalCount, isPlaceholderAllSelected)
                : ''
            }
            tProps={
              labelTKey
                ? {
                    label: t(labelTKey),
                    item: t(labelTKey),
                    count: selectedIDs.size,
                    selection: getSelectionValue(),
                  }
                : {}
            }
          />
        </Col>
        <Col displayFlex style={{ flexShrink: 0 }}>
          <CaretMiniDownIcon color={publicaColors.gray} />
        </Col>
      </Cols>
      <Popover
        id={Boolean(anchorEl) ? 'independentselect-popover' : undefined}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore // MUI WON'T ALLOW AN EXTRA PROP
        onClose={handleOnClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          style: {
            overflow: 'hidden',
            width: `${popoverWidth}px`,
            border: `1px solid ${publicaColors.lightGray}`,
            boxShadow: 'none',
          },
        }}
      >
        <Rows>
          {/* STICKY SECTION */}
          <Row
            noPadding
            style={{
              borderBottom: `1px solid ${publicaColors.lightGray}`,
            }}
          >
            <Rows>
              {!disableSearch && (
                <Row smallPadding>
                  <TextInput
                    enableClear
                    fullWidth
                    handleOnChange={(value: string) => setSearch(value.toLowerCase())}
                    inputSize='small'
                    isAutoFocused
                    msDelay={500}
                    placeholder={t('Search')}
                    prefix='search'
                    inputID={''}
                  />
                </Row>
              )}
              <Row style={{ padding: disableSearch ? '8px 8px 6px' : '0px 8px 6px' }}>
                <Cols style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <Col flex>
                    <Cols gap={spacing.base} style={{ alignItems: 'center' }}>
                      {(!selectedLimit ||
                        (selectedLimit && filteredlistCount <= selectedLimit)) && (
                        <Col>
                          <TextButton
                            size='small'
                            onClick={selectedCount < totalCount ? handleSelectAll : handleClear}
                          >
                            {t('SelectAll')}
                          </TextButton>
                        </Col>
                      )}
                      <Col>
                        <Text muted small disableSelect>
                          {`${selectedCount} /${selectedMax} ${t('Selected')}`}
                        </Text>
                      </Col>
                      {enableActiveFilter && selectedActiveStatus > EActiveFilter.SHOW_ALL && (
                        <Col>
                          <Text
                            highlighted
                            extraSmall
                            disableSelect
                            tKey={
                              statuses.find(
                                (status) => status.value === Number(selectedActiveStatus),
                              )?.messageTKey || ''
                            }
                          />
                        </Col>
                      )}
                    </Cols>
                  </Col>
                  <Cols
                    gap={spacing.base}
                    style={{ justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    {!isSingleSelectionRequired && selectedCount > 0 && (
                      <Col style={{ paddingTop: '1px' }}>
                        <TextButton size='small' onClick={handleClear}>
                          {t('Clear')}
                        </TextButton>
                      </Col>
                    )}
                    {enableActiveFilter && (
                      <Col displayFlex>
                        <ActiveQuickFilter
                          selectedStatus={selectedActiveStatus}
                          handleChange={handleChangeActiveFilter}
                        />
                      </Col>
                    )}
                  </Cols>
                </Cols>
              </Row>
              {presets && (
                <Row style={{ padding: '0px 8px 6px' }}>
                  <Cols gap={spacing.base} style={{ alignItems: 'center' }}>
                    {presets.map((preset) => (
                      <Col>
                        <TextButton
                          size={isOversizedLanguage ? 'extraSmall' : 'small'}
                          onClick={() => handleSelectPreset(preset.subset)}
                        >
                          {preset.tKey ? t(preset.tKey) : preset.label}
                        </TextButton>
                      </Col>
                    ))}
                  </Cols>
                </Row>
              )}
            </Rows>
          </Row>

          {/* LIST SECTION */}
          {filteredlistCount ? (
            <Row noPadding>
              <FixedSizeList
                height={getHeight(filteredlistCount)}
                itemCount={filteredlistCount}
                itemSize={30}
                width={popoverWidth}
              >
                {({ index, style }) =>
                  VirtualSelectRow({
                    index,
                    style,
                    displayID,
                    handleOnChange,
                    options: filteredOptions,
                    selectedIDs,
                    enableLabelTooltip,
                    isCheckboxHidden,
                    textWidth: textWidth || undefined,
                  })
                }
              </FixedSizeList>
            </Row>
          ) : (
            <Row style={{ textAlign: 'center' }}>
              <Text small tKey='NoResultsFound' />
            </Row>
          )}
          {errorMessageTKey && (
            <Row
              noPadding
              style={{
                borderTop: `1px solid ${publicaColors.lightGray}`,
              }}
            >
              <Text
                small
                color={publicaColors.red}
                style={{ padding: spacing.small }}
                tKey={errorMessageTKey}
              />
            </Row>
          )}
        </Rows>
      </Popover>
    </>
  );
};
